import {
  GetQBankStructureByIdDocument,
  GetQBankStructureLevelItemsByQBankStructureIdDocument,
  GetQBankStructureLevelsByQBankStructureIdDocument,
  QBankStructureStatus,
  useSaveQBankStructureAsDraftMutation,
  useUpdateQBankStructureLevelsByQBankStructureIdMutation,
} from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { Box, Button, Flex, SettingsOutlinedIcon, Text } from '@shared/components';
import { WithPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { QBankStructureLevelPopover } from '../../components';
import {
  QBANK_STRUCTURE_LEVEL_ITEM_FORM_ACTION_QUERY_KEY,
  QBankStructureLevelItemFormAction,
} from '../../constants';

import QBankStructureLevelForm, {
  type QBankStructureLevelStatusByCategory,
} from './qbank-structure-level-form';
import QBankStructureLevelItemListAction from './qbank-structure-level-item-list-action';

export default function QBankStructureLevelItemListHeader() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { qBankStructureInformation, qBankStructureLevelItems, qBankStructureLevels } =
    useQBankStructure();
  const [, setSearchParams] = useSearchParams();
  const { showSnackbar } = useSnackbar();

  const shouldShowQBankStructureLevelSettings =
    qBankStructureInformation?.status === QBankStructureStatus.Draft;

  const [saveQBankStructureAsDraft, { loading: isSaveQBankStructureAsDraftLoading }] =
    useSaveQBankStructureAsDraftMutation({
      onCompleted: () => {
        showSnackbar({
          message: 'บันทึกแบบร่างแล้ว',
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: 'ไม่สามารถบันทึกแบบร่างได้',
          variant: 'error',
        });
      },
    });

  const [updateQBankStructureLevels, { loading: isUpdateQBankStructureLevelsLoading }] =
    useUpdateQBankStructureLevelsByQBankStructureIdMutation({
      onCompleted: () => {
        showSnackbar({
          message: 'อัปเดต ระดับโครงสร้าง เรียบร้อยแล้ว',
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: 'อัปเดต ระดับโครงสร้าง ไม่สำเร็จ',
          variant: 'error',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQBankStructureLevelsByQBankStructureIdDocument,
          variables: {
            qBankStructureId: qBankStructureInformation.id,
          },
        },
        {
          query: GetQBankStructureLevelItemsByQBankStructureIdDocument,
          variables: {
            qBankStructureId: qBankStructureInformation.id,
            options: null,
          },
        },
        {
          query: GetQBankStructureByIdDocument,
          variables: {
            id: qBankStructureInformation.id,
          },
        },
      ],
    });

  const handleClickEdit = () => {
    setSearchParams({
      [QBANK_STRUCTURE_LEVEL_ITEM_FORM_ACTION_QUERY_KEY]: QBankStructureLevelItemFormAction.EDIT,
    });
  };

  const handleClickSetting = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSubmitLevel = async (formValues: QBankStructureLevelStatusByCategory) => {
    const levelItemsForSaveAsDraftInput = qBankStructureLevelItems.map((levelItem) => {
      return {
        id: levelItem.id,
        order: levelItem.order,
        parentLevelItemId: levelItem.parentLevelItemId,
        qBankStructureLevelId: levelItem.qBankStructureLevelId,
        tag: levelItem.tag?.name ? { name: levelItem.tag.name } : undefined,
      };
    });
    const saveAsDraftResult = await saveQBankStructureAsDraft({
      variables: {
        input: {
          levelItems: levelItemsForSaveAsDraftInput,
          qBankStructureId: qBankStructureInformation.id,
        },
      },
    });

    if (saveAsDraftResult.data) {
      const updateLevelsResult = await updateQBankStructureLevels({
        variables: {
          input: {
            qBankStructureId: qBankStructureInformation.id,
            qBankStructureLevels: qBankStructureLevels.map(({ id, category }) => ({
              id,
              enabled: formValues[category] ?? true,
            })),
          },
        },
      });

      if (updateLevelsResult.data) {
        handleClose();
      }
    }
  };

  return (
    <Flex sx={(theme) => ({ justifyContent: 'space-between', width: 1, p: theme.spacing(4, 6) })}>
      <Flex
        sx={{
          alignItems: 'center',
          gap: (theme) => theme.spacing(2),
          flex: 1,
          justifyContent: 'space-between',
        }}
      >
        <Text variant='h3'>QBank Structure</Text>
        {shouldShowQBankStructureLevelSettings ? (
          <WithPermission requiredPermission={APP_PERMISSIONS.QBANK.STRUCTURE.UPDATE}>
            <Button
              color='secondary'
              variant='outlined'
              onClick={handleClickSetting}
              startIcon={<SettingsOutlinedIcon />}
            >
              Edit Structure Level
            </Button>
            <QBankStructureLevelPopover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleClose}
            >
              <QBankStructureLevelForm
                onClose={handleClose}
                onSubmit={handleSubmitLevel}
                loading={isSaveQBankStructureAsDraftLoading || isUpdateQBankStructureLevelsLoading}
              />
            </QBankStructureLevelPopover>
          </WithPermission>
        ) : null}
      </Flex>
      <Box>
        <QBankStructureLevelItemListAction onEdit={handleClickEdit} />
      </Box>
    </Flex>
  );
}
